import { useCallback, useEffect, useRef, useState } from 'react'

export interface AsyncState<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
  /** Replaces the held value without a round trip, after a successful write. */
  set: (value: T) => void
}

/**
 * Loads once per dependency change and exposes exactly the three outcomes the
 * screens have to render: loading, error, or data.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const run = useRef(0)
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    const ticket = ++run.current
    setLoading(true)
    setError(null)
    loadRef
      .current()
      .then((value) => {
        if (ticket !== run.current) return
        setData(value)
      })
      .catch((err: unknown) => {
        if (ticket !== run.current) return
        setError(err instanceof Error ? err.message : 'Something did not respond.')
      })
      .finally(() => {
        if (ticket === run.current) setLoading(false)
      })
    // deps are supplied by the caller and intentionally spread
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  const set = useCallback((value: T) => setData(value), [])

  return { data, error, loading, reload, set }
}

/** Debounces a value so typing in the search box does not fire a call per key. */
export function useDebounced<T>(value: T, ms = 220): T {
  const [held, setHeld] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setHeld(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return held
}
